import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { isDespiaIOS } from "@/hooks/use-despia";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { EntitlementProvider } from "@/components/EntitlementProvider";
// Soft paywall: AuthGate gates the app shell (auth only). Entitlement is checked
// at the content level (reader/audio paywall hooks). See .lovable/plan.md.
import { AuthGate } from "@/components/AuthGate";
import { RevenueCatIdentityProvider } from "@/components/RevenueCatIdentityProvider";
import { Toaster } from "@/components/ui/toaster";

import { PWAUpdateNotifier } from "@/components/PWAUpdateNotifier";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetworkStatusIndicator } from "@/components/NetworkStatusIndicator";
import { useCapacitorInit } from "@/hooks/use-capacitor-init";
import { useAppLifecycle } from "@/hooks/use-app-lifecycle";
import { SharedElementProvider } from "@/contexts/SharedElementContext";
import { RoxboxPlayerProvider } from "@/contexts/RoxboxPlayerContext";
import { GlobalMiniPlayer } from "@/components/roxbox/GlobalMiniPlayer";
import { AppReadinessProvider } from "@/contexts/AppReadinessContext";
import { AppLoadingGate } from "@/components/AppLoadingGate";
import { AppFlowProvider } from "@/contexts/AppFlowContext";
import { AppFlowOverlay } from "@/components/AppFlowOverlay";

import { initAppleAuth } from "@/lib/apple-auth";

// V2 — main app shell
const V2Profile = lazy(() => import("@/pages/v2/V2Profile"));
import { V2Layout } from "@/components/v2/V2Layout";

// Auth callback handler (Apple OAuth)
const AuthLoading = lazy(() => import("@/pages/AuthLoading"));

// Public flow pages
const V2Auth = lazy(() => import("@/pages/v2/V2Auth"));
const V2Onboarding = lazy(() => import("@/pages/v2/V2Onboarding"));
const V2Subscribe = lazy(() => import("@/pages/v2/V2Subscribe"));

// Utility pages
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const ShareReceiver = lazy(() => import("@/pages/ShareReceiver"));
const LoadingPage = lazy(() => import("@/components/LoadingPage").then(m => ({ default: m.LoadingPage })));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Resolves Despia bundleNumber on native iOS cold boot before rendering the
// app shell, so window.bundleNumber is always defined for downstream code.
// On web/non-native, renders children immediately.
function NativeBootHydrator({ children }: { children: React.ReactNode }) {
  const isNativeIOS = isDespiaIOS();
  const [versionResolved, setVersionResolved] = useState(!isNativeIOS);

  useEffect(() => {
    if (!isNativeIOS) return;
    if (typeof (window as any).bundleNumber === 'string') {
      setVersionResolved(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const despia = (await import('despia-native')).default;
        const info = await despia('getappversion://', ['versionNumber', 'bundleNumber']);
        if (!cancelled && info?.bundleNumber) {
          (window as any).bundleNumber = info.bundleNumber;
          (window as any).versionNumber = info.versionNumber;
        }
      } catch (e) {
        console.warn('Failed to fetch Despia version:', e);
      }
      if (!cancelled) setVersionResolved(true);
    })();
    return () => { cancelled = true; };
  }, [isNativeIOS]);

  if (!versionResolved) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-4">
        <div className="p-1.5 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl drop-shadow-lg animate-pulse">
          <img
            src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/thicker_.png"
            alt="BuxBox"
            className="h-16 w-auto"
          />
        </div>
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return <>{children}</>;
}

// Backward-compat: any /v2/* deep link (push notifications, native cold-starts,
// shared URLs from earlier versions) strips the /v2 prefix and redirects to the
// equivalent root path. Keep indefinitely.
function V2LegacyRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/v2/, '') || '/';
  return <Navigate to={newPath + location.search + location.hash} replace />;
}


function AppContent() {
  useCapacitorInit();
  useAppLifecycle();
  const location = useLocation();

  // Initialize Apple JS SDK on mount
  useEffect(() => {
    initAppleAuth();
  }, []);

  // Bind $appsflyerId to the anonymous RevenueCat subscriber as early as
  // possible. BuxBox is paywall-before-signup, so this MUST happen before the
  // user reaches the paywall — otherwise RC fires the purchase event to
  // AppsFlyer without a UID and Meta never sees the conversion.
  useEffect(() => {
    void import('@/lib/revenuecat-attributes').then(({ syncAppsFlyerIdToRevenueCatEarly }) => {
      void syncAppsFlyerIdToRevenueCatEarly('app_launch');
    });
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = async (event: MessageEvent) => {
        // Reserved for future sync functionality
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  useEffect(() => {
    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const preventDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    const preventCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('dragstart', preventDragStart);
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventDragStart);
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
    };
  }, []);


  

  return (
    <>

      <PWAUpdateNotifier />
      <NetworkStatusIndicator />

      <div id="main-content">
        <Suspense fallback={null}>
          <Routes>
            {/* Apple OAuth callbacks */}
            <Route path="/auth-loading" element={<AuthLoading />} />
            <Route path="/auth/apple/callback" element={<AuthLoading />} />

            {/* Public flow pages — handle their own auth/onboarding routing */}
            <Route path="/auth" element={<V2Auth />} />
            <Route path="/onboarding" element={<V2Onboarding />} />
            <Route path="/subscribe" element={<V2Subscribe />} />

            {/* Auth-gated app shell */}
            <Route path="/" element={
              <AuthGate>
                <NativeBootHydrator>
                  <V2Layout />
                </NativeBootHydrator>
              </AuthGate>
            } />
            <Route path="/profile" element={
              <AuthGate>
                <V2Profile />
              </AuthGate>
            } />

            {/* Backward-compat: legacy /v2/* deep links → /* */}
            <Route path="/v2" element={<Navigate to="/" replace />} />
            <Route path="/v2/*" element={<V2LegacyRedirect />} />

            {/* Utility routes */}
            <Route path="/share" element={<ShareReceiver />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}


/**
 * Renders paid-app global UI (mini player, profile/flow overlay) on the paid
 * surface for any signed-in user. Subscription gating moved to content-level
 * paywall hooks (see .lovable/plan.md).
 */
function GatedPaidGlobals() {
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const path = location.pathname;

  const PUBLIC_PREFIXES = [
    '/auth', '/onboarding', '/subscribe',
    '/auth-loading', '/auth/apple/callback',
    '/share', '/refund-policy', '/privacy-policy', '/terms-of-service',
  ];
  const isPublic = PUBLIC_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
  if (isPublic || authLoading || !user) return null;

  return (
    <>
      <AppFlowOverlay />
      <GlobalMiniPlayer />
    </>
  );
}

function App() {

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <RevenueCatIdentityProvider>
              <EntitlementProvider>
                <AppReadinessProvider>
                  <AppFlowProvider>
                    <SharedElementProvider>
                      <RoxboxPlayerProvider>
                        <AppLoadingGate>
                          <AppContent />
                        </AppLoadingGate>
                        {/* Paid-app global UI — only on paid surface, only when entitled. */}
                        <GatedPaidGlobals />
                        <Toaster />



                      </RoxboxPlayerProvider>
                    </SharedElementProvider>
                  </AppFlowProvider>
                </AppReadinessProvider>
              </EntitlementProvider>
            </RevenueCatIdentityProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
